import React, { useContext, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { gql, useQuery, useMutation } from "@apollo/client";
import Spinner from "../../components/Spinner";
import StarekContext from "../../context/StarekContext";
import RoleSidebar from "./RoleSidebar";
import EntityOperationBar from "../../components/EntityOperationBar";
import ConfirmModal from "../../components/modals/ConfirmModal";
import "../../starek.css";

// UI Strings
const UI_STRINGS = {
    PAGE_TITLE: "Roles",
    SEARCH_PLACEHOLDER: "Search",
    // Delete confirmation
    DELETE_TITLE: "Delete Role(s)?",
    DELETE_CONFIRM: "Delete",
    DELETE_CANCEL: "Cancel",
    // Success messages
    DELETE_SUCCESS: "Role(s) deleted successfully",
    // Error messages
    DELETE_ERROR: "Failed to delete role(s)",
};

// Confirmation messages
const ROLE_MESSAGES = {
    DELETE_ROLE_SINGLE: (name) => `You are about to delete role "${name}".

This action cannot be undone.

Are you sure you wish to continue?`,

    DELETE_ROLES_BULK: (count) => `You are about to delete ${count} role(s).

This action cannot be undone.

Are you sure you wish to continue?`,
};

const GET_ROLES = gql`
    query Roles {
        roles {
            id
            name
            description
            is_active
            memberships {
                role_id
            }
            privileges {
                id
                entity_type_id
                permission
            }
        }
    }
`;

const DELETE_ONE_ROLE = gql`
    mutation DeleteOneRole($id: Int!) {
        deleteOneRole(where: { id: $id }) {
            id
        }
    }
`;

const DELETE_MANY_ROLE = gql`
    mutation DeleteManyRole($ids: [Int!]!) {
        deleteManyRole(where: { id: { in: $ids } }) {
            count
        }
    }
`;

const Roles = () => {
    const starekContext = useContext(StarekContext);

    const { data, loading, error, refetch } = useQuery(GET_ROLES, {
        client: starekContext.engineClient,
        fetchPolicy: "network-only",
    });

    const [items, setItems] = useState([]);
    const [filterText, setFilterText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [showWizard, setShowWizard] = useState(false);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });

    const [deleteOneRole, { loading: deletingOne }] = useMutation(
        DELETE_ONE_ROLE,
        {
            client: starekContext.engineClient,
        },
    );
    const [deleteManyRole, { loading: deletingMany }] = useMutation(
        DELETE_MANY_ROLE,
        {
            client: starekContext.engineClient,
        },
    );

    const isDeleting = deletingOne || deletingMany;

    useEffect(() => {
        if (error) starekContext.showError(error);
    }, [error]);

    useEffect(() => {
        if (!loading && data?.roles) {
            setItems(data.roles || []);
        }
    }, [data, loading]);

    const filteredItems = useMemo(() => {
        const q = filterText.trim().toLowerCase();
        if (!q) return items;
        return items.filter((r) => {
            return (
                (r.name || "").toLowerCase().includes(q) ||
                (r.description || "").toLowerCase().includes(q)
            );
        });
    }, [items, filterText]);

    const handleAdd = () => {
        setSelectedRows([]);
        setToggleCleared((p) => !p);
        setShowWizard(true);
    };

    const handleEdit = () => {
        if (selectedRows.length === 1) {
            setShowWizard(true);
        }
    };

    const handleDelete = () => {
        if (selectedRows.length === 0) return;

        const count = selectedRows.length;
        const roleName = selectedRows[0]?.name || "Unknown";

        setConfirmCfg({
            open: true,
            type: "danger",
            title: UI_STRINGS.DELETE_TITLE,
            content:
                count === 1
                    ? ROLE_MESSAGES.DELETE_ROLE_SINGLE(roleName)
                    : ROLE_MESSAGES.DELETE_ROLES_BULK(count),
            confirmText: UI_STRINGS.DELETE_CONFIRM,
            cancelText: UI_STRINGS.DELETE_CANCEL,
            onConfirm: async () => {
                const ids = selectedRows.map((r) => Number(r.id));

                try {
                    if (ids.length === 1) {
                        await deleteOneRole({ variables: { id: ids[0] } });
                    } else {
                        await deleteManyRole({ variables: { ids } });
                    }
                    starekContext?.showSuccess?.(UI_STRINGS.DELETE_SUCCESS);
                } catch (error) {
                    starekContext.showError(
                        error?.message || UI_STRINGS.DELETE_ERROR,
                    );
                } finally {
                    setSelectedRows([]);
                    setToggleCleared((p) => !p);
                    await refetch();
                    setConfirmCfg({ open: false });
                }
            },
            onClose: () => setConfirmCfg({ open: false }),
        });
    };

    const handleDrawerClose = async () => {
        setShowWizard(false);
        setSelectedRows([]);
        setToggleCleared((p) => !p);
        await refetch();
    };

    const handleSaveRole = async () => {
        await handleDrawerClose();
    };

    const handleDeleteRole = async (role) => {
        if (!role?.id) return;

        const roleName = role.name || "Unknown";
        setConfirmCfg({
            open: true,
            type: "danger",
            title: UI_STRINGS.DELETE_TITLE,
            content: ROLE_MESSAGES.DELETE_ROLE_SINGLE(roleName),
            confirmText: UI_STRINGS.DELETE_CONFIRM,
            cancelText: UI_STRINGS.DELETE_CANCEL,
            onConfirm: async () => {
                try {
                    await deleteOneRole({ variables: { id: Number(role.id) } });
                    starekContext?.showSuccess?.(UI_STRINGS.DELETE_SUCCESS);
                } catch (error) {
                    starekContext.showError(
                        error?.message || UI_STRINGS.DELETE_ERROR,
                    );
                } finally {
                    await handleDrawerClose();
                    setConfirmCfg({ open: false });
                }
            },
            onClose: () => setConfirmCfg({ open: false }),
        });
    };

    const columns = [
        {
            name: "Name",
            selector: (r) => r.name || "-",
            sortable: true,
            grow: 1.2,
            cell: (row) => (
                <div className="text-primary">{row.name || "-"}</div>
            ),
        },
        {
            name: "Description",
            selector: (r) => r.description || "-",
            sortable: false,
            grow: 2,
            cell: (row) => (
                <div className="text-truncate" style={{ maxWidth: 520 }}>
                    {row.description || "-"}
                </div>
            ),
        },
    ];

    const customStyles = {
        headCells: { style: { fontWeight: "bold" } },
        rows: { style: { minHeight: "60px" } },
        cells: {
            style: {
                paddingTop: "12px",
                paddingBottom: "12px",
                whiteSpace: "normal",
                lineHeight: "1.2rem",
            },
        },
    };

    if (loading) {
        return (
            <div
                className="d-flex flex-column justify-content-center align-items-center"
                style={{ minHeight: "100vh" }}
            >
                <div className="text-xs d-flex justify-content-center">
                    <i className="fa fa-lock fa-10x text-success"></i>
                </div>
                <Spinner />
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">{UI_STRINGS.PAGE_TITLE}</div>

            <EntityOperationBar
                entity={{ singular: "Role", plural: "Roles" }}
                allowSearch={true}
                allowAdd={true}
                allowDelete={true}
                allowEdit={true}
                selectedRows={selectedRows}
                isDeleting={isDeleting}
                searchPlaceholder={UI_STRINGS.SEARCH_PLACEHOLDER}
                onSearch={(text) => setFilterText(text)}
                onAdd={handleAdd}
                onViewChange={() => {}}
                onDelete={handleDelete}
                onEdit={handleEdit}
            >
                <EntityOperationBar.View
                    mode="list"
                    icon="fa-list"
                    tooltip="List View"
                >
                    <DataTable
                        columns={columns}
                        data={filteredItems}
                        selectableRows
                        onSelectedRowsChange={({ selectedRows }) =>
                            setSelectedRows(selectedRows)
                        }
                        clearSelectedRows={toggleCleared}
                        highlightOnHover
                        pointerOnHover
                        responsive
                        pagination
                        dense
                        selectableRowsHighlight
                        selectableRowsNoSelectAll={false}
                        customStyles={customStyles}
                    />
                </EntityOperationBar.View>
            </EntityOperationBar>

            <RoleSidebar
                show={showWizard}
                item={selectedRows.length === 1 ? selectedRows[0] : null}
                onDiscard={handleDrawerClose}
                onSave={handleSaveRole}
                onDelete={handleDeleteRole}
            />

            <ConfirmModal
                open={confirmCfg.open}
                type={confirmCfg.type || "danger"}
                title={confirmCfg.title}
                content={confirmCfg.content}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                loading={isDeleting}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
            />
        </div>
    );
};

export { Roles };
